import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function PublicCheckinPage() {
  const { classId } = useParams()
  const [cls, setCls] = useState(null)
  const [fighters, setFighters] = useState([])
  const [checkedInIds, setCheckedInIds] = useState([])
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState(null) // null | 'loading' | 'success' | 'already' | 'error'
  const [checkedInFighter, setCheckedInFighter] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const [clsRes, cisRes] = await Promise.all([
        supabase.from('classes')
          .select('*, class_techniques(*, techniques(*)), clubs(*)')
          .eq('id', classId).single(),
        supabase.from('checkins')
          .select('fighter_id')
          .eq('class_id', classId)
          .gte('checked_in_at', new Date().toISOString().split('T')[0]),
      ])
      if (clsRes.data) {
        setCls(clsRes.data)
        const fightersRes = await supabase.from('fighters')
          .select('*').eq('club_id', clsRes.data.club_id).order('first_name')
        setFighters(fightersRes.data || [])
      }
      setCheckedInIds((cisRes.data || []).map(c => c.fighter_id))
      setLoading(false)
    }
    load()
  }, [classId])

  async function checkIn(fighter) {
    if (checkedInIds.includes(fighter.id)) {
      setStatus('already'); setCheckedInFighter(fighter); return
    }
    setStatus('loading'); setCheckedInFighter(fighter)

    await supabase.from('checkins').insert({ fighter_id: fighter.id, class_id: classId })
    await supabase.from('fighters').update({
      sessions_count: (fighter.sessions_count || 0) + 1,
      belt_sessions: (fighter.belt_sessions || 0) + 1,
    }).eq('id', fighter.id)

    const techIds = cls?.class_techniques?.map(ct => ct.technique_id) || []
    for (const techId of techIds) {
      const { data: ex } = await supabase.from('fighter_skills').select('*')
        .eq('fighter_id', fighter.id).eq('technique_id', techId).single()
      if (ex && ex.stars < 10) await supabase.from('fighter_skills').update({ stars: ex.stars + 1 }).eq('id', ex.id)
      else if (!ex) await supabase.from('fighter_skills').insert({ fighter_id: fighter.id, technique_id: techId, stars: 1 })
    }

    setCheckedInIds(prev => [...prev, fighter.id])
    setStatus('success')
  }

  const techs = cls?.class_techniques?.map(ct => ct.techniques?.name).filter(Boolean) || []
  const filtered = fighters.filter(f =>
    !search || (f.first_name + ' ' + f.last_name).toLowerCase().includes(search.toLowerCase())
  )

  if (loading) return <Screen><p style={{ color: '#888' }}>Loading...</p></Screen>
  if (!cls) return <Screen><p style={{ color: '#c00' }}>Session not found.</p></Screen>

  if (status === 'success') {
    return (
      <Screen>
        <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🥋</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1a7040', marginBottom: 8 }}>
            Checked in!
          </div>
          <div style={{ fontSize: 16, color: '#333', marginBottom: 4 }}>
            {checkedInFighter?.first_name} {checkedInFighter?.last_name}
          </div>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 24 }}>
            {cls.name}
          </div>
          {techs.length > 0 && (
            <div style={{ background: '#eaf7f0', borderRadius: 12, padding: '12px 20px', display: 'inline-block' }}>
              <div style={{ fontSize: 12, color: '#1a7040', fontWeight: 600, marginBottom: 6 }}>Stars earned today</div>
              {techs.map(t => (
                <div key={t} style={{ fontSize: 13, color: '#1a7040' }}>⭐ {t}</div>
              ))}
            </div>
          )}
          <button onClick={() => setStatus(null)} style={{
            display: 'block', margin: '24px auto 0',
            background: 'transparent', border: '1px solid #ddd', borderRadius: 8,
            padding: '8px 20px', fontSize: 13, color: '#888', cursor: 'pointer'
          }}>
            Check in another fighter
          </button>
        </div>
      </Screen>
    )
  }

  if (status === 'already') {
    return (
      <Screen>
        <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#333', marginBottom: 8 }}>
            Already checked in
          </div>
          <div style={{ fontSize: 14, color: '#888', marginBottom: 20 }}>
            {checkedInFighter?.first_name} is already in today's session
          </div>
          <button onClick={() => setStatus(null)} style={{
            background: '#1860a8', color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 24px', fontSize: 14, cursor: 'pointer'
          }}>
            Back
          </button>
        </div>
      </Screen>
    )
  }

  return (
    <Screen>
      {/* Club + class header */}
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>{cls.clubs?.name}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a' }}>{cls.name}</div>
        <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
          {cls.time_of_day}{techs.length > 0 ? ' · ' + techs.join(', ') : ''}
        </div>
        <div style={{ fontSize: 12, color: '#1a7040', marginTop: 6 }}>
          {checkedInIds.length} checked in so far
        </div>
      </div>

      <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a', marginBottom: 10 }}>
        Tap your name to check in
      </div>

      <input
        value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search your name..."
        style={{
          width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #ddd',
          fontSize: 15, marginBottom: 12, boxSizing: 'border-box', outline: 'none'
        }}
        autoFocus
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map(f => {
          const done = checkedInIds.includes(f.id)
          return (
            <button key={f.id} onClick={() => !done && checkIn(f)} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              background: done ? '#f0faf4' : '#fff',
              border: `1px solid ${done ? '#b8e8cc' : '#e8e8e8'}`,
              borderRadius: 12, padding: '12px 16px', cursor: done ? 'default' : 'pointer',
              textAlign: 'left', width: '100%', fontFamily: 'inherit',
              transition: 'background 0.15s'
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                background: done ? '#b8e8cc' : '#eef4ff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 13,
                color: done ? '#1a7040' : '#1860a8'
              }}>
                {done ? '✓' : f.first_name[0] + f.last_name[0]}
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>
                  {f.first_name} {f.last_name}
                </div>
                <div style={{ fontSize: 11, color: '#888' }}>
                  {done ? 'Checked in' : f.type === 'kids' ? 'Kids' : 'Adult'}
                </div>
              </div>
            </button>
          )
        })}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa', fontSize: 14 }}>
            No fighters found
          </div>
        )}
      </div>
    </Screen>
  )
}

function Screen({ children }) {
  return (
    <div style={{
      minHeight: '100vh', background: '#f4f6fb',
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      padding: '1.5rem 1rem', maxWidth: 480, margin: '0 auto'
    }}>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: '#fff', borderRadius: 12, padding: '8px 16px', border: '1px solid #eee'
        }}>
          <span style={{ fontSize: 18 }}>🥋</span>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a' }}>MyAthleteCard</span>
        </div>
      </div>
      {children}
    </div>
  )
}
